import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";

import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Autocomplete from "@material-ui/lab/Autocomplete";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
import Box from "@material-ui/core/Box";
import Collapse from "@material-ui/core/Collapse";
import CloseIcon from "@material-ui/icons/Close";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PersonIcon from "@material-ui/icons/Person";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
	...getStandardButtonStyles(theme),
	dialogPaper: {
		backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
		borderRadius: 16,
		border: `1px solid ${theme.palette.primary.main}40`,
		boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
		color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
		display: "flex",
		flexDirection: "column",
		maxHeight: "90vh",
	},
	dialogHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: theme.spacing(2, 3),
		borderBottom: `1px solid ${
			theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
		}`,
		"& h2": {
			display: "flex",
			alignItems: "center",
			fontSize: "1.15rem",
			fontWeight: 600,
			margin: 0,
			color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
		},
	},
	helpBox: {
		backgroundColor:
			theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
		borderBottom: `1px solid ${
			theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
		}`,
		padding: theme.spacing(1.5, 3),
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},
	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		marginBottom: theme.spacing(1),
	},
	formContainer: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
		overflow: "hidden",
	},
	contentArea: {
		flex: 1,
		overflowY: "auto",
		padding: theme.spacing(2.5, 3),
		...theme.scrollbarStyles,
	},
	dialogActions: {
		padding: theme.spacing(2, 3),
		borderTop: `1px solid ${
			theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
		}`,
	},
}));

const formatDateForInput = (date) => {
	if (!date) return "";
	if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
		return date;
	}
	const d = new Date(date);
	if (isNaN(d.getTime())) return "";
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
	if (!dateString) return null;
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
		return dateString;
	}
	if (dateString.includes("T")) {
		return dateString.split("T")[0];
	}
	return dateString;
};

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, i18n.t("contactModal.validations.tooShort"))
		.max(250, i18n.t("contactModal.validations.tooLong"))
		.required(i18n.t("contactModal.validations.required")),
	number: Yup.string()
		.min(8, i18n.t("contactModal.validations.tooShort"))
		.max(50, i18n.t("contactModal.validations.tooLong")),
	email: Yup.string().email(i18n.t("contactModal.validations.invalidEmail")),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const theme = useTheme();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		disableBot: false,
		lgpdAcceptedAt: "",
		birthDate: "",
		extraInfo: []
	};

	const [contact, setContact] = useState(initialState);
	const [showHelp, setShowHelp] = useState(false);
	const [disableBot, setDisableBot] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState("");
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [options, setOptions] = useState([]);
	const [loadingUsers, setLoadingUsers] = useState(false);
	const [searchParam, setSearchParam] = useState("");

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Cargar filas completas al abrir el modal
	useEffect(() => {
		if (open) {
			const fetchQueues = async () => {
				try {
					const { data } = await api.get("/queue");
					if (isMounted.current) {
						setAllQueues(data || []);
						setQueues(data || []);
					}
				} catch (err) {
					toastError(err);
				}
			};
			fetchQueues();
		}
	}, [open]);

	// Cargar todos los agentes/usuarios al abrir o cuando busca
	useEffect(() => {
		if (!open) return;

		const fetchUsers = async () => {
			setLoadingUsers(true);
			try {
				const { data } = await api.get("/users/", {
					params: { searchParam: searchParam || "" },
				});
				if (isMounted.current) {
					setOptions(data.users || []);
				}
			} catch (err) {
				toastError(err);
			} finally {
				if (isMounted.current) {
					setLoadingUsers(false);
				}
			}
		};

		const delayDebounceFn = setTimeout(() => {
			fetchUsers();
		}, 300);

		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, open]);

	// Cargar datos del contacto
	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact((prevState) => ({
					...prevState,
					...initialValues,
					extraInfo: Array.isArray(initialValues.extraInfo) ? initialValues.extraInfo : [],
					birthDate: formatDateForInput(initialValues.birthDate)
				}));
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact({
						...data,
						extraInfo: Array.isArray(data.extraInfo) ? data.extraInfo : [],
						birthDate: formatDateForInput(data.birthDate)
					});
					setDisableBot(Boolean(data.disableBot));

					if (data.contactWallets && data.contactWallets.length > 0) {
						const wallet = data.contactWallets[0].wallet;
						const queue = data.contactWallets[0].queue;

						if (wallet) {
							setSelectedUser({
								id: wallet.id,
								name: wallet.name,
							});
						}
						if (queue) {
							setSelectedQueue(queue.id);
						}
					} else {
						setSelectedUser(null);
						setSelectedQueue("");
					}
				}
			} catch (err) {
				toastError(err);
			}
		};

		if (open) {
			fetchContact();
		}
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setSelectedUser(null);
		setSelectedQueue("");
		setSearchParam("");
		setShowHelp(false);
	};

	const handleSaveContact = async (values) => {
		try {
			const contactData = {
				...values,
				disableBot: disableBot,
				birthDate: parseDateFromInput(values.birthDate)
			};

			if (contactId) {
				if (!selectedUser && !selectedQueue) {
					delete contact.contactWallets;
					await api.delete(`/contacts/wallet/${contactId}`);
				}

				delete contact.contactWallets;
				delete contactData.contactWallets;

				await api.put(`/contacts/${contactId}`, contactData);

				if (selectedUser && selectedQueue) {
					await api.put(`/contacts/wallet/${contactId}`, {
						wallets: {
							userId: selectedUser.id,
							queueId: selectedQueue,
						},
					});
				}

				handleClose();
			} else {
				delete contactData.contactWallets;

				const { data } = await api.post("/contacts", contactData);

				if (data.id && selectedUser && selectedQueue) {
					await api.put(`/contacts/wallet/${data.id}`, {
						wallets: {
							userId: selectedUser.id,
							queueId: selectedQueue,
						},
					});
				}

				if (onSave) {
					onSave(data);
				}

				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			scroll="paper"
			PaperProps={{
				className: classes.dialogPaper,
			}}
		>
			<div className={classes.dialogHeader}>
				<h2>
					<PersonIcon style={{ marginRight: 10, fontSize: 22, color: "#00b4d8" }} />
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</h2>
				<Box display="flex" alignItems="center">
					<IconButton
						size="small"
						onClick={() => setShowHelp((prev) => !prev)}
						title="Ayuda / Información"
						style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
					>
						<HelpOutlineIcon fontSize="small" />
					</IconButton>
					<Typography
						variant="caption"
						style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
					>
						BotMix | CRM
					</Typography>
					<IconButton size="small" onClick={handleClose} aria-label="Cerrar">
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>
			</div>

			<Collapse in={showHelp}>
				<Box className={classes.helpBox}>
					<Typography
						variant="body2"
						style={{
							color: theme.palette.type === "dark" ? "#90e0ef" : "#0077b6",
							lineHeight: 1.5,
						}}
					>
						{i18n.t("contactModal.helpSummary") ||
							"Administra los datos personales, etiquetas de segmentación, asignación de cartera de agentes y fila correspondiente para este contacto."}
					</Typography>
				</Box>
			</Collapse>

			<Formik
				initialValues={contact}
				enableReinitialize={true}
				validationSchema={ContactSchema}
				onSubmit={(values, actions) => {
					setTimeout(() => {
						handleSaveContact(values);
						actions.setSubmitting(false);
					}, 300);
				}}
			>
				{({ values, errors, touched, isSubmitting, setFieldValue }) => (
					<Form className={classes.formContainer}>
						<DialogContent dividers className={classes.contentArea}>
							<Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12 }}>
								{i18n.t("contactModal.form.mainInfo")}
							</Typography>

							<Field
								as={TextField}
								label={i18n.t("contactModal.form.name")}
								name="name"
								autoFocus
								error={touched.name && Boolean(errors.name)}
								helperText={touched.name && errors.name}
								variant="outlined"
								margin="dense"
								fullWidth
							/>

							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.number")}
										name="number"
										error={touched.number && Boolean(errors.number)}
										helperText={touched.number && errors.number}
										placeholder="51999999999"
										variant="outlined"
										margin="dense"
										fullWidth
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<TextField
										label={i18n.t("contactModal.form.lid")}
										value={contact.lid || ""}
										disabled
										fullWidth
										margin="dense"
										variant="outlined"
										helperText={i18n.t("contactModal.form.lidHelper")}
									/>
								</Grid>
							</Grid>

							<Box mt={1}>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.email")}
									name="email"
									error={touched.email && Boolean(errors.email)}
									helperText={touched.email && errors.email}
									placeholder={i18n.t("contactModal.form.emailPlaceholder")}
									fullWidth
									margin="dense"
									variant="outlined"
								/>
							</Box>

							<Box mt={1}>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.birthDate")}
									name="birthDate"
									type="date"
									InputLabelProps={{
										shrink: true,
									}}
									fullWidth
									margin="dense"
									variant="outlined"
									helperText={i18n.t("contactModal.form.birthDateHelper")}
									onChange={(e) => {
										const formattedDate = parseDateFromInput(e.target.value);
										setFieldValue("birthDate", formattedDate);
									}}
								/>
							</Box>

							{/* Etiquetas de Contacto */}
							<Box mt={2}>
								<Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
									{i18n.t("tags.title") || "Etiquetas de Contacto"}
								</Typography>
								<TagsContainer contact={contact} />
							</Box>

							{/* Asignación a Cartera de Agente y Fila */}
							<Box mt={2}>
								<Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
									{i18n.t("contactModal.form.assignWallet") || "Asignar a Cartera de Agente y Fila"}
								</Typography>
								<Grid container spacing={2}>
									<Grid xs={12} sm={6} item>
										<Autocomplete
											value={selectedUser}
											fullWidth
											getOptionLabel={(option) => `${option?.name || ""}`}
											onChange={(e, newValue) => {
												setSelectedUser(newValue);
												if (newValue != null && Array.isArray(newValue.queues) && newValue.queues.length > 0) {
													if (newValue.queues.length === 1) {
														setSelectedQueue(newValue.queues[0].id);
													}
													setQueues(newValue.queues);
												} else {
													setQueues(allQueues);
												}
											}}
											options={options}
											getOptionSelected={(option, val) => option?.id === val?.id}
											loading={loadingUsers}
											noOptionsText={i18n.t("transferTicketModal.noOptions")}
											renderInput={(params) => (
												<TextField
													{...params}
													label={i18n.t("transferTicketModal.fieldLabel") || "Seleccione o busque un agente"}
													variant="outlined"
													margin="dense"
													onChange={(e) => setSearchParam(e.target.value)}
													InputProps={{
														...params.InputProps,
														endAdornment: (
															<React.Fragment>
																{loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
																{params.InputProps.endAdornment}
															</React.Fragment>
														),
													}}
												/>
											)}
										/>
									</Grid>

									<Grid xs={12} sm={6} item>
										<FormControl variant="outlined" margin="dense" fullWidth>
											<InputLabel>
												{i18n.t("transferTicketModal.fieldQueueLabel") || "Fila / Departamento"}
											</InputLabel>
											<Select
												value={selectedQueue || ""}
												onChange={(e) => setSelectedQueue(e.target.value)}
												label={i18n.t("transferTicketModal.fieldQueueLabel") || "Fila / Departamento"}
											>
												<MenuItem value="">
													<em>{i18n.t("common.none", "Ninguna")}</em>
												</MenuItem>
												{(queues.length > 0 ? queues : allQueues).map((queue) => (
													<MenuItem key={queue.id} value={queue.id}>
														{queue.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
								</Grid>
							</Box>

							{/* Switch Desactivar Chatbot */}
							<Box mt={2} mb={1}>
								<Box display="flex" alignItems="center">
									<Switch
										size="small"
										checked={disableBot}
										onChange={() => setDisableBot(!disableBot)}
										name="disableBot"
										color="primary"
									/>
									<Typography variant="body2" style={{ fontWeight: 600, marginLeft: 8 }}>
										{i18n.t("contactModal.form.chatBotContact") || "Desactivar Chatbot para este contacto"}
									</Typography>
								</Box>
								<Typography variant="caption" style={{ color: theme.palette.text.secondary, display: "block", marginLeft: 44, marginTop: 2 }}>
									{i18n.t("contactModal.form.chatBotContactHelp") ||
										"Si se activa, el chatbot y los flujos automáticos de IA no responderán a este contacto; la atención será 100% manual por agentes."}
								</Typography>
							</Box>

							{/* Información de WhatsApp y LGPD */}
							{contact?.whatsapp && (
								<Typography variant="caption" style={{ display: "block", color: theme.palette.text.secondary, marginTop: 6 }}>
									{i18n.t("contactModal.form.whatsapp")} {contact.whatsapp.name}
								</Typography>
							)}

							{/* Información Adicional */}
							<Box mt={2}>
								<Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
									{i18n.t("contactModal.form.extraInfo") || "Información Adicional"}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div className={classes.extraAttr} key={`${index}-info`}>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName") || "Nombre del campo"}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue") || "Valor"}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton size="small" onClick={() => remove(index)} title="Eliminar campo">
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<Button
												fullWidth
												variant="outlined"
												className={classes.actionOutlineBtn}
												style={{ marginTop: 8 }}
												onClick={() => push({ name: "", value: "" })}
											>
												{`+ ${i18n.t("contactModal.buttons.addExtraInfo") || "Agregar campo adicional"}`}
											</Button>
										</>
									)}
								</FieldArray>
							</Box>
						</DialogContent>

						<DialogActions className={classes.dialogActions}>
							<Button onClick={handleClose} disabled={isSubmitting} className={classes.dangerButton}>
								{i18n.t("contactModal.buttons.cancel")}
							</Button>
							<Button type="submit" disabled={isSubmitting} className={classes.applyButton}>
								{contactId
									? `${i18n.t("contactModal.buttons.okEdit")}`
									: `${i18n.t("contactModal.buttons.okAdd")}`}
								{isSubmitting && <CircularProgress size={20} style={{ color: "#fff", marginLeft: 8 }} />}
							</Button>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default ContactModal;
